import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { VueQueryPlugin, QueryClient } from '@tanstack/vue-query';
import * as Sentry from '@sentry/vue';
import { createGtag } from 'vue-gtag';
import { createHead } from '@unhead/vue/client';

import App from './App.vue';
import router from './router';
import i18n from './i18n';
import './plugins/axios';
import sanitizePlugin from './plugins/sanitize';
import '@/assets/main.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (failureCount >= 3) return false;

        const status = error?.response?.status;
        if (!status) return true;

        const shouldRetry = status === 408 || status === 429 || (status >= 500 && status < 600);

        return shouldRetry;
      },
      retryDelay: (attemptIndex) => {
        const baseDelay = 1000;
        const maxDelay = 10000;
        const exponentialDelay = Math.min(baseDelay * Math.pow(2, attemptIndex), maxDelay);
        const jitter = Math.random() * 500;
        return exponentialDelay + jitter;
      },
      staleTime: 1000 * 60,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

// Handle Vite preload errors (e.g. after a new deployment)
if (import.meta.env.MODE !== 'test') {
  window.addEventListener('vite:preloadError', (event) => {
    console.error('Vite preload error detected, reloading page...', event.payload);
    window.location.reload();
  });
}

const app = createApp(App);

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN || '',
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
  ],
  ignoreErrors: [
    'Unable to preload CSS',
    'Failed to fetch dynamically imported module',
  ],
  tracesSampleRate: 1.0,
  environment: import.meta.env.MODE,
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/app\.lokerjambi\.co\.id/,
    /^https:\/\/blog\.lokerjambi\.co\.id/
  ],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

app.use(createPinia());
app.use(router);
app.use(i18n);
app.use(sanitizePlugin);
app.use(createHead());
app.use(VueQueryPlugin, { queryClient });
app.use(createGtag({
  tagId: import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX',
  enabled: import.meta.env.MODE === 'production',
  pageTracker: {
    router,
  },
}));

app.mount('#app');

import 'flowbite';
